import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants/lib';
import { ActionAPI, FieldAPI, FormAPI, ModalProps, ModalService, ToastService } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Inject } from '@odinjs/odin';
import moment from 'moment';
import { DressingsResponse, WoundsDressingTabType } from '../../../types/wounds-dressings-types';
import BaseController from '../../../../BaseController';
import { DressingsViewerAliases, FragmentNames } from '../../../../../codegen/index';
import { FULL_LOCALE_DATE } from '../../../../../constants/date-utils';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import { Endpoint } from '../../../../../constants/endpoints'
import DialogService from '../../../../../services/dialog-service';
import { WoundsDressingsTabListContext } from '../../../service/wounds-dressing-tab-list-service';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';

@Controller({ component: DressingsViewerAliases.LAYOUT_DRESSINGS_VIEWER, fragment: FragmentNames.DRESSINGS_VIEWER})
class DressingsViewerController extends BaseController {

  @Inject(DressingsViewerAliases.FORM_DRESSINGS_VIEWER)
  private formDressingsViewerApi!: FormAPI;

  @Inject(DressingsViewerAliases.FIELD_ENCOUNTER)
  private fieldEncounterApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_RECORD_DATE)
  private fieldRecordDateApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_RELEASE_DATE)
  private fieldReleaseDateApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_SECTOR)
  private fieldSectorApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_TOTAL_SCORE)
  private fieldTotalScoreApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_WIDTH_CM)
  private fieldWidthCmApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_LENGTH_CM)
  private fieldLengthCmApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_DEPTH_CM)
  private fieldDepthCmApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_TIME_MIN)
  private fieldTimeMinApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_DEGREE_OR_STAGE)
  private fieldDegreeOrStageApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_VALIDITY_HOURS)
  private fieldValidityHoursApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_NOTE)
  private fieldNoteApi!: FieldAPI;

  @Inject(DressingsViewerAliases.FIELD_DRESSING_DRESCRIPTION_RESULT)
  private fieldResultDescriptionApi!: FieldAPI;

  @Inject(DressingsViewerAliases.ACTION_INACTIVATE_DRESSING)
  private actionInactivateAPI!: ActionAPI;

  @Inject(DressingsViewerAliases.ACTION_CANCEL_DRESSING)
  private actionCancelDressing!: ActionAPI;

  @Inject(DressingsViewerAliases.ACTION_RELEASE_DRESSING)
  private actionReleaseAPI!: ActionAPI;

  private openModalInactivation(): void {
    const { dressingId } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'dressings-inactivate',
      id: 'dressings-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      dressingId
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  private handleVisibleFields(record: DressingsResponse): void{
    this.fieldEncounterApi.setVisible(BooleanUtils.isNotNull(record.encounterId));
    this.fieldRecordDateApi.setVisible(BooleanUtils.isNotNull(record.recordDate));
    this.fieldReleaseDateApi.setVisible(BooleanUtils.isNotNull(record.releaseDate));
    this.fieldTotalScoreApi.setVisible(BooleanUtils.isNotNull(record.totalScore));
    this.fieldWidthCmApi.setVisible(BooleanUtils.isNotNull(record?.widthCm));
    this.fieldLengthCmApi.setVisible(BooleanUtils.isNotNull(record?.lenghtCm));
    this.fieldDepthCmApi.setVisible(BooleanUtils.isNotNull(record?.depthCm));
    this.fieldTimeMinApi.setVisible(BooleanUtils.isNotNull(record?.timeMinutes));
    this.fieldDegreeOrStageApi.setVisible(BooleanUtils.isNotNull(record?.degreeOrStageDesc));
    this.fieldValidityHoursApi.setVisible(BooleanUtils.isNotNull(record?.validityHours));
    this.fieldNoteApi.setVisible(BooleanUtils.isNotNull(record?.notes));
    this.fieldResultDescriptionApi.setVisible(BooleanUtils.isNotNull(record?.result));
    this.fieldSectorApi.setVisible(BooleanUtils.isNotNull(record?.sectorDesc));
  }

  private setAllowEditing(res: DressingsResponse): void {
    const isAllowEditingDetail = Boolean(res?.isAllowEditing) ;
    const allowInfo = InformationBarUtils.allowInfo && isAllowEditingDetail;
    const showInactivate = Boolean(res?.showInactivate) && allowInfo;

    this.actionInactivateAPI.setEnabled(showInactivate);
    this.actionCancelDressing.setEnabled(allowInfo && !BooleanUtils.isNotNull(res.releaseDate));
    this.actionReleaseAPI.setEnabled(allowInfo && !res.releaseDate)
  }

  private handleFetchWounds(): Promise<void> {
    const { dressingId }  = this.historyService.getCurrentRouteParams();

    return HttpService.get(Endpoint.DRESSING, { dressingId })
      .then(response => {
        const res = response.body as DressingsResponse;
        const recordDate     = moment(res.recordDate).local().format(FULL_LOCALE_DATE);
        const releaseDate    = moment(res.releaseDate).local().format(FULL_LOCALE_DATE);

        this.context.set({ dressings: res })
        this.formDressingsViewerApi.setData({...res, recordDate, releaseDate });
        this.handleVisibleFields(res);
        this.setAllowEditing(res);
      })
      .catch((error) => this.showToast(error?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formDressingsViewerApi.setLoading(false);
      });
  }

  @OnMount({target: DressingsViewerAliases.LAYOUT_DRESSINGS_VIEWER})
  handleMountLayout(): void {
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.DRESSINGS;
    const { encounterId, patientId, dressingId } = this.historyService.getCurrentRouteParams();
    this.context.set({ dressingId, encounterId, patientId });
  }

  @OnMount({target: DressingsViewerAliases.FORM_DRESSINGS_VIEWER})
  handleMountForm(): void {
    this.formDressingsViewerApi.setLoading(true);
    this.handleFetchWounds();
  }

  @OnUnmount(DressingsViewerAliases.LAYOUT_DRESSINGS_VIEWER)
  handleUnmountLayout(): void {
    WoundsDressingsTabListContext.clean();
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.DRESSINGS;
  }

  @OnClick(DressingsViewerAliases.ACTION_INACTIVATE_DRESSING)
  handleClickInactivate(): void {
    this.openModalInactivation();
  }

  @OnClick(DressingsViewerAliases.ACTION_CANCEL_DRESSING)
  handleClickCancel(): void {
    const { dressingId } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    this.confirmDelete(dressingId as number);
  }

  private delete(dressingId: number): void {
    HttpService.delete(Endpoint.DRESSING, { dressingId })
      .then(() => {
        this.historyService.back();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      })
  }

  private confirmDelete(dressingId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => {  this.historyService.back(); },
      onClickOk: () => { this.delete(dressingId)},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'wounds-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  @OnClick(DressingsViewerAliases.ACTION_RELEASE_DRESSING)
  handleClickRelease(): void {
    const { dressingId } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(dressingId as number);
  }

  private showModalSubmit(dressingId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { applicationContext.popNavigationItem(); },
      onClickOk: () => { this.handleReleaseDressing(dressingId) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    })
  }

  private handleReleaseDressing(dressingId: number): void {
    this.formDressingsViewerApi.setLoading(true);
    HttpService.put(Endpoint.RELEASE_DRESSING, { dressingId })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => {
        this.formDressingsViewerApi.setLoading(false);
      })
  }
}
export default DressingsViewerController;
